/* eslint-disable @typescript-eslint/no-use-before-define */
import { QuestQuestionDTO } from './QuestQuestionDTO';
import { Breast, AnswerChoices } from '@/models/VariousEnum';
import moment from 'moment';

export class QuestQuestion {
  private _lastMammo: LastMammo;
  private _hormonalTreatment: HormonalTreatment;
  private _familyCancer: FamilyCancer;
  private _breastSymptoms: BreastSymptoms;
  private _breastOperations: BreastOperations;
  private _prosthesisCode: string|null;

  constructor(questions: QuestQuestionDTO) {
    this._lastMammo = new LastMammo(questions);
    this._hormonalTreatment = new HormonalTreatment(questions);
    this._familyCancer = new FamilyCancer(questions);
    this._breastSymptoms = new BreastSymptoms(questions);
    this._breastOperations = new BreastOperations(questions);
    this._prosthesisCode = questions.prosthesisCode;
  }

  get lastMammo(): LastMammo {
    return this._lastMammo;
  }
  set lastMammo(value: LastMammo) {
    this._lastMammo = value;
  }

  get hormonalTreatment(): HormonalTreatment {
    return this._hormonalTreatment;
  }
  set hormonalTreatment(value: HormonalTreatment) {
    this._hormonalTreatment = value;
  }

  get familyCancer(): FamilyCancer {
    return this._familyCancer;
  }
  set familyCancer(value: FamilyCancer) {
    this._familyCancer = value;
  }

  get breastSymptoms(): BreastSymptoms {
    return this._breastSymptoms;
  }
  set breastSymptoms(value: BreastSymptoms) {
    this._breastSymptoms = value;
  }

  get breastOperations(): BreastOperations {
    return this._breastOperations;
  }
  set breastOperations(value: BreastOperations) {
    this._breastOperations = value;
  }
}

export class LastMammo {
  flagLastMammo: string|null;
  lastMammoDate: Date|null;
  examCenterId: number|null;
  lastMammoPlace: string|null;
  mammoReasonCode: string|null;

  constructor(questions: QuestQuestionDTO) {
    this.flagLastMammo = questions.flagLastMammo;
    this.lastMammoDate = questions.lastMammoDate;
    this.examCenterId = questions.examCenterId;
    this.lastMammoPlace = questions.lastMammoPlace;
    this.mammoReasonCode = questions.mammoReasonCode;
  }

  private prepareForDto() {
    if (this.flagLastMammo != AnswerChoices.Yes) {
      this.lastMammoDate = null;
      this.examCenterId = null;
      this.lastMammoPlace = null;
      this.mammoReasonCode = null;
    }
  }

  public toDto(): object {
    this.prepareForDto();
    return {
      flagLastMammo: this.flagLastMammo,
      lastMammoDate: this.lastMammoDate,
      examCenterId: this.examCenterId,
      lastMammoPlace: this.lastMammoPlace,
      mammoReasonCode: this.mammoReasonCode,
    };
  }
}

export class HormonalTreatment {
  flagHormonalTreatment: string|null;
  treatmentDuration: number|null;
  flagHormonalTreatmentBefore: string|null;
  hormonalTreatmentStartDate: Date|null;
  hormonalTreatmentEndDate: Date|null;
  hormonalTreatmentSince: number|null = null;
  hormonalTreatmentBeforeSince: number|null = null;
  hormonalTreatmentBeforeDuration: number|null = null;

  constructor(questions: QuestQuestionDTO) {
    this.flagHormonalTreatment = questions.flagHormonalTreatment;
    this.treatmentDuration = questions.treatmentDuration;
    this.flagHormonalTreatmentBefore = questions.flagHormonalTreatmentBefore;
    this.hormonalTreatmentStartDate = questions.hormonalTreatmentStartDate;
    this.hormonalTreatmentEndDate = questions.hormonalTreatmentEndDate;
    // initialize hormonalTreatmentSince, hormonalTreatmentBeforeSince and hormonalTreatmentBeforeDuration
    // based on treatmentDuration, hormonalTreatmentStartDate, hormonalTreatmentEndDate values
    this.computeHormonalTreatmentSince();
  }

  private prepareForDto() {
    if (this.flagHormonalTreatment == AnswerChoices.DontKnow) {
      this.treatmentDuration = null;
      this.flagHormonalTreatmentBefore = null;
      this.hormonalTreatmentSince = null;
      this.hormonalTreatmentStartDate = null;
      this.hormonalTreatmentEndDate = null;
    } else {
      this.computeTreatmentStartDateAndDuration();
      this.computeTreatmentBeforeDatesAndDuration();
    }
  }

  /**
   * Compute the values of hormonalTreatmentSince and hormonalTreatmentBeforeSince
   * based on the provided dates and answered questions when initializing the object the first time.
   */
  private computeHormonalTreatmentSince(): void {
    if (this.hormonalTreatmentStartDate !== null) {
      
      if (this.flagHormonalTreatment == AnswerChoices.Yes) {
        // difference between current date and start date of treatment
        const treatmentStart = moment(this.hormonalTreatmentStartDate).startOf('day');
        const since = moment().utc().startOf('day').diff(treatmentStart, 'years', true);
        this.hormonalTreatmentSince = Math.round(since * 100) / 100;
        this.hormonalTreatmentBeforeSince = null;
        this.treatmentDuration = null;
      }

      if (this.flagHormonalTreatment == AnswerChoices.No && this.flagHormonalTreatmentBefore == AnswerChoices.BeforeHT_NoSince) {
        // difference between current date and end date of treatment
        const treatmentEnd = moment(this.hormonalTreatmentEndDate).startOf('day') || moment().utc().startOf('day');
        const beforeSince = moment().utc().startOf('day').diff(treatmentEnd, 'years', true);
        this.hormonalTreatmentBeforeSince = Math.round(beforeSince * 100) / 100;
        this.hormonalTreatmentBeforeDuration = this.treatmentDuration;
        this.hormonalTreatmentSince = null;
      }
      return;
    }
    this.hormonalTreatmentSince = null;
  }

  /**
   * Compute the different dates when specifying since how many years a treatment is followed (current situation)
   * in order to update the DTO accordingly.
   */
  private computeTreatmentStartDateAndDuration(): void {
    if (this.flagHormonalTreatment == AnswerChoices.Yes) {
      if (this.hormonalTreatmentSince) {
        const computedDate = moment().utc().startOf('day')
          .subtract(this.hormonalTreatmentSince, 'years');

        if (this.hormonalTreatmentStartDate) {
          const currentDate = moment(this.hormonalTreatmentStartDate).utc().startOf('day');
          if (computedDate.isSame(currentDate, 'year') && computedDate.isSame(currentDate, 'month') && computedDate.isSame(currentDate, 'day')) {
            // same date, so don't touch hormonalTreatmentStartDate
          } else {
            // update the date if it's different
            this.hormonalTreatmentStartDate = new Date(computedDate.format('YYYY-MM-DD'));
          }
        } else {
          this.hormonalTreatmentStartDate = new Date(computedDate.format('YYYY-MM-DD'));
        }
      } else {
        this.hormonalTreatmentStartDate = null;
      }

      this.hormonalTreatmentEndDate = null;
      this.treatmentDuration = null;
    }
  }

  /**
   * Compute the different dates when specifying since how many years a treatment was followed (past situation)
   * in order to update the DTO accordingly.
   */
  private computeTreatmentBeforeDatesAndDuration(): void {
    if (this.flagHormonalTreatment == AnswerChoices.No) {
      if (this.flagHormonalTreatmentBefore == AnswerChoices.BeforeHT_NoSince) {
        if (this.hormonalTreatmentBeforeSince && this.hormonalTreatmentBeforeDuration) {

          this.treatmentDuration = this.hormonalTreatmentBeforeDuration;

          const computedStartDate = moment().utc().startOf('day')
            .subtract(this.hormonalTreatmentBeforeSince, 'years')
            .subtract(this.hormonalTreatmentBeforeDuration, 'years');
          const computedEndDate = moment().utc().startOf('day')
            .subtract(this.hormonalTreatmentBeforeSince, 'years');

          // Set the start date
          if (this.hormonalTreatmentStartDate) {
            const currentDate = moment(this.hormonalTreatmentStartDate).utc().startOf('day');
            if (computedStartDate.isSame(currentDate, 'year') && computedStartDate.isSame(currentDate, 'month') && computedStartDate.isSame(currentDate, 'day')) {
              // same date, so don't touch hormonalTreatmentStartDate
            } else {
              // update the date if it's different
              this.hormonalTreatmentStartDate = new Date(computedStartDate.format('YYYY-MM-DD'));
            }
          } else {
            // Set the date the first time
            this.hormonalTreatmentStartDate = new Date(computedStartDate.format('YYYY-MM-DD'));
          }

          // Set the end date
          if (this.hormonalTreatmentEndDate) {
            const currentDate = moment(this.hormonalTreatmentEndDate).utc().startOf('day');
            if (computedEndDate.isSame(currentDate, 'year') && computedEndDate.isSame(currentDate, 'month') && computedEndDate.isSame(currentDate, 'day')) {
              // same date, so don't touch hormonalTreatmentEndDate
            } else {
              // update the date if it's different
              this.hormonalTreatmentEndDate = new Date(computedEndDate.format('YYYY-MM-DD'));
            }
          } else {
            // Set the date the first time
            this.hormonalTreatmentEndDate = new Date(computedEndDate.format('YYYY-MM-DD'));
          }

        } else {
          this.hormonalTreatmentStartDate = null;
          this.hormonalTreatmentEndDate = null;
        }
      }
      if (this.flagHormonalTreatmentBefore == AnswerChoices.BeforeHT_No || this.flagHormonalTreatmentBefore == AnswerChoices.BeforeHT_NoAnswer) {
        this.treatmentDuration = null;
        this.hormonalTreatmentStartDate = null;
        this.hormonalTreatmentEndDate = null;
      }
    }
  }

  public toDto(): object {
    this.prepareForDto();
    return {
      flagHormonalTreatment: this.flagHormonalTreatment,
      treatmentDuration: this.treatmentDuration ? Number(this.treatmentDuration) : null,
      flagHormonalTreatmentBefore: this.flagHormonalTreatmentBefore,
      hormonalTreatmentStartDate: this.hormonalTreatmentStartDate,
      hormonalTreatmentEndDate: this.hormonalTreatmentEndDate,
    };
  }
}

export class FamilyCancer {
  flagParentBreastCancer: string|null;
  motherCancerCode: string|null;
  sisterCancerCode: string|null;
  daughterCancerCode: string|null;
  parentCancerCode: string|null;
  parentCancerDetails: string|null;

  constructor(questions: QuestQuestionDTO) {
    this.flagParentBreastCancer = questions.flagParentBreastCancer;
    this.motherCancerCode = questions.motherCancerCode;
    this.sisterCancerCode = questions.sisterCancerCode;
    this.daughterCancerCode = questions.daughterCancerCode;
    this.parentCancerCode = questions.parentCancerCode;
    this.parentCancerDetails = questions.parentCancerDetails;
  }

  private prepareForDto() {
    if (this.flagParentBreastCancer == AnswerChoices.Yes) {
      this.parentCancerDetails = this.parentCancerCode ? this.parentCancerDetails : null
    } else {
      this.motherCancerCode = null;
      this.sisterCancerCode = null;
      this.daughterCancerCode = null;
      this.parentCancerCode = null;
      this.parentCancerDetails = null;
    }
  }

  public toDto(): object {
    this.prepareForDto();
    return {
      flagParentBreastCancer: this.flagParentBreastCancer,
      motherCancerCode: this.motherCancerCode,
      sisterCancerCode: this.sisterCancerCode,
      daughterCancerCode: this.daughterCancerCode,
      parentCancerCode: this.parentCancerCode,
      parentCancerDetails: this.parentCancerDetails,
    };
  }
}

export class BreastSymptoms {
  flagCurrentProblem: string|null;
  currentProblemPainCode: string|null;
  currentProblemPainLBChecked: boolean = false;
  currentProblemPainRBChecked: boolean = false;
  currentProblemFlowCode: string|null;
  currentProblemFlowLBChecked: boolean = false;
  currentProblemFlowRBChecked: boolean = false;
  currentProblemBouleCode: string|null;
  currentProblemBouleLBChecked: boolean = false;
  currentProblemBouleRBChecked: boolean = false;
  currentProblemOtherCode: string|null;
  currentProblemOtherLBChecked: boolean = false;
  currentProblemOtherRBChecked: boolean = false;
  currentProblemOther: string|null;

  constructor(questions: QuestQuestionDTO) {
    this.flagCurrentProblem = questions.flagCurrentProblem;
    this.currentProblemPainCode = questions.currentProblemPainCode;
    if (this.currentProblemPainCode) {
      switch (this.currentProblemPainCode) {
        case Breast.Both:
          this.currentProblemPainLBChecked = true;
          this.currentProblemPainRBChecked = true;
          break;

        case Breast.Right:
          this.currentProblemPainRBChecked = true;
          break

        case Breast.Left:
          this.currentProblemPainLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.currentProblemFlowCode = questions.currentProblemFlowCode;
    if (this.currentProblemFlowCode) {
      switch (this.currentProblemFlowCode) {
        case Breast.Both:
          this.currentProblemFlowLBChecked = true;
          this.currentProblemFlowRBChecked = true;
          break;

        case Breast.Right:
          this.currentProblemFlowRBChecked = true;
          break

        case Breast.Left:
          this.currentProblemFlowLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.currentProblemBouleCode = questions.currentProblemBouleCode;
    if (this.currentProblemBouleCode) {
      switch (this.currentProblemBouleCode) {
        case Breast.Both:
          this.currentProblemBouleLBChecked = true;
          this.currentProblemBouleRBChecked = true;
          break;

        case Breast.Right:
          this.currentProblemBouleRBChecked = true;
          break

        case Breast.Left:
          this.currentProblemBouleLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.currentProblemOtherCode = questions.currentProblemOtherCode;
    if (this.currentProblemOtherCode) {
      switch (this.currentProblemOtherCode) {
        case Breast.Both:
          this.currentProblemOtherLBChecked = true;
          this.currentProblemOtherRBChecked = true;
          break;

        case Breast.Right:
          this.currentProblemOtherRBChecked = true;
          break

        case Breast.Left:
          this.currentProblemOtherLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.currentProblemOther = questions.currentProblemOther;
  }

  private getSurgeryCode(leftBreast: boolean, rightBreast: boolean): string|null {
    if (leftBreast && rightBreast) {
      return Breast.Both;
    }
    if (leftBreast && !rightBreast) {
      return Breast.Left;
    }
    if (!leftBreast && rightBreast) {
      return Breast.Right;
    }
    // !leftBreast && !rightBreast
    return null;
    
  }

  private prepareForDto() {
    if (this.flagCurrentProblem == AnswerChoices.Yes) {
      this.currentProblemPainCode = this.getSurgeryCode(this.currentProblemPainLBChecked, this.currentProblemPainRBChecked);
      this.currentProblemFlowCode = this.getSurgeryCode(this.currentProblemFlowLBChecked, this.currentProblemFlowRBChecked);
      this.currentProblemBouleCode = this.getSurgeryCode(this.currentProblemBouleLBChecked, this.currentProblemBouleRBChecked);
      this.currentProblemOtherCode = this.getSurgeryCode(this.currentProblemOtherLBChecked, this.currentProblemOtherRBChecked);
      this.currentProblemOther = this.currentProblemOtherCode ? this.currentProblemOther : null
    } else {
      this.currentProblemPainCode = null
      this.currentProblemFlowCode = null;
      this.currentProblemBouleCode = null;
      this.currentProblemOtherCode = null;
      this.currentProblemOther = null;
    }
  }

  public toDto(): object {
    this.prepareForDto();
    return {
      flagCurrentProblem: this.flagCurrentProblem,
      currentProblemPainCode: this.currentProblemPainCode,
      currentProblemFlowCode: this.currentProblemFlowCode,
      currentProblemBouleCode: this.currentProblemBouleCode,
      currentProblemOtherCode: this.currentProblemOtherCode,
      currentProblemOther: this.currentProblemOther,
    };
  }
}

export class BreastOperations {
  flagBreastSurgery: string|null;
  benignAffectionSurgeryCode: string|null;
  benignAffectionSurgeryLBChecked: boolean = false;
  benignAffectionSurgeryRBChecked: boolean = false;
  benignAffectionSurgeryDetails: string|null;
  benignAffectionSurgeryYear: number|null;
  breastCancerSurgeryCode: string|null;
  breastCancerSurgeryLBChecked: boolean = false;
  breastCancerSurgeryRBChecked: boolean = false;
  breastCancerSurgeryDetails: string|null;
  breastCancerSurgeryYear: number|null;
  plasticSurgeryCode: string|null;
  plasticSurgeryLBChecked: boolean = false;
  plasticSurgeryRBChecked: boolean = false;
  plasticSurgeryDetails: string|null;
  plasticSurgeryYear: number|null;
  otherProblemSurgeryCode: string|null;
  otherProblemSurgeryLBChecked: boolean = false;
  otherProblemSurgeryRBChecked: boolean = false;
  otherProblemSurgeryDetails: string|null;
  otherProblemSurgeryYear: number|null;

  constructor(questions: QuestQuestionDTO) {
    this.flagBreastSurgery = questions.flagBreastSurgery;

    this.benignAffectionSurgeryCode = questions.benignAffectionSurgeryCode;
    if (this.benignAffectionSurgeryCode) {
      switch (this.benignAffectionSurgeryCode) {
        case Breast.Both:
          this.benignAffectionSurgeryLBChecked = true;
          this.benignAffectionSurgeryRBChecked = true;
          break;

        case Breast.Right:
          this.benignAffectionSurgeryRBChecked = true;
          break

        case Breast.Left:
          this.benignAffectionSurgeryLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.plasticSurgeryCode = questions.plasticSurgeryCode;
    if (this.plasticSurgeryCode) {
      switch (this.plasticSurgeryCode) {
        case Breast.Both:
          this.plasticSurgeryLBChecked = true;
          this.plasticSurgeryRBChecked = true;
          break;

        case Breast.Right:
          this.plasticSurgeryRBChecked = true;
          break

        case Breast.Left:
          this.plasticSurgeryLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.breastCancerSurgeryCode = questions.breastCancerSurgeryCode;
    if (this.breastCancerSurgeryCode) {
      switch (this.breastCancerSurgeryCode) {
        case Breast.Both:
          this.breastCancerSurgeryLBChecked = true;
          this.breastCancerSurgeryRBChecked = true;
          break;

        case Breast.Right:
          this.breastCancerSurgeryRBChecked = true;
          break

        case Breast.Left:
          this.breastCancerSurgeryLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.otherProblemSurgeryCode = questions.otherProblemSurgeryCode;
    if (this.otherProblemSurgeryCode) {
      switch (this.otherProblemSurgeryCode) {
        case Breast.Both:
          this.otherProblemSurgeryLBChecked = true;
          this.otherProblemSurgeryRBChecked = true;
          break;

        case Breast.Right:
          this.otherProblemSurgeryRBChecked = true;
          break

        case Breast.Left:
          this.otherProblemSurgeryLBChecked = true;
          break;
      
        default:
          break;
      }
    }

    this.benignAffectionSurgeryDetails = questions.benignAffectionSurgeryDetails;
    this.benignAffectionSurgeryYear = questions.benignAffectionSurgeryYear;
    this.breastCancerSurgeryDetails = questions.breastCancerSurgeryDetails;
    this.breastCancerSurgeryYear = questions.breastCancerSurgeryYear;
    this.plasticSurgeryDetails = questions.plasticSurgeryDetails;
    this.plasticSurgeryYear = questions.plasticSurgeryYear;
    this.otherProblemSurgeryDetails = questions.otherProblemSurgeryDetails;
    this.otherProblemSurgeryYear = questions.otherProblemSurgeryYear;
  }

  private getSurgeryCode(leftBreast: boolean, rightBreast: boolean): string|null {
    if (leftBreast && rightBreast) {
      return Breast.Both;
    }
    if (leftBreast && !rightBreast) {
      return Breast.Left;
    }
    if (!leftBreast && rightBreast) {
      return Breast.Right;
    }
    // !leftBreast && !rightBreast
    return null;
    
  }

  private prepareForDto() {
    if (this.flagBreastSurgery == AnswerChoices.Yes) {
      this.benignAffectionSurgeryCode = this.getSurgeryCode(this.benignAffectionSurgeryLBChecked, this.benignAffectionSurgeryRBChecked);
      this.breastCancerSurgeryCode = this.getSurgeryCode(this.breastCancerSurgeryLBChecked, this.breastCancerSurgeryRBChecked);
      this.plasticSurgeryCode = this.getSurgeryCode(this.plasticSurgeryLBChecked, this.plasticSurgeryRBChecked);
      this.otherProblemSurgeryCode = this.getSurgeryCode(this.otherProblemSurgeryLBChecked, this.otherProblemSurgeryRBChecked);

      this.benignAffectionSurgeryYear = (this.benignAffectionSurgeryCode && this.benignAffectionSurgeryYear) ? Number(this.benignAffectionSurgeryYear) : null;
      this.breastCancerSurgeryYear = (this.breastCancerSurgeryCode && this.breastCancerSurgeryYear) ? Number(this.breastCancerSurgeryYear) : null;
      this.plasticSurgeryYear = (this.plasticSurgeryCode && this.plasticSurgeryYear) ? Number(this.plasticSurgeryYear) : null;
      this.otherProblemSurgeryYear = (this.otherProblemSurgeryCode && this.otherProblemSurgeryYear) ? Number(this.otherProblemSurgeryYear) : null;

      this.benignAffectionSurgeryDetails = (this.benignAffectionSurgeryCode && this.benignAffectionSurgeryDetails) ? this.benignAffectionSurgeryDetails : null;
      this.breastCancerSurgeryDetails = (this.breastCancerSurgeryCode && this.breastCancerSurgeryDetails) ? this.breastCancerSurgeryDetails : null;
      this.plasticSurgeryDetails = (this.plasticSurgeryCode && this.plasticSurgeryDetails) ? this.plasticSurgeryDetails : null;
      this.otherProblemSurgeryDetails = (this.otherProblemSurgeryCode && this.otherProblemSurgeryDetails) ? this.otherProblemSurgeryDetails : null;      
    } else {
      this.benignAffectionSurgeryCode = null
      this.benignAffectionSurgeryDetails = null;
      this.benignAffectionSurgeryYear = null;
      this.breastCancerSurgeryCode = null;
      this.breastCancerSurgeryDetails = null;
      this.breastCancerSurgeryYear = null;
      this.plasticSurgeryCode = null;
      this.plasticSurgeryDetails = null;
      this.plasticSurgeryYear = null;
      this.otherProblemSurgeryCode = null;
      this.otherProblemSurgeryDetails = null;
      this.otherProblemSurgeryYear = null;
    }
  }

  public toDto(): object {
    this.prepareForDto();
    return {
      flagBreastSurgery: this.flagBreastSurgery,
      benignAffectionSurgeryCode: this.benignAffectionSurgeryCode,
      benignAffectionSurgeryDetails: this.benignAffectionSurgeryDetails,
      benignAffectionSurgeryYear: this.benignAffectionSurgeryYear,
      breastCancerSurgeryCode: this.breastCancerSurgeryCode,
      breastCancerSurgeryDetails: this.breastCancerSurgeryDetails,
      breastCancerSurgeryYear: this.breastCancerSurgeryYear,
      plasticSurgeryCode: this.plasticSurgeryCode,
      plasticSurgeryDetails: this.plasticSurgeryDetails,
      plasticSurgeryYear: this.plasticSurgeryYear,
      otherProblemSurgeryCode: this.otherProblemSurgeryCode,
      otherProblemSurgeryDetails: this.otherProblemSurgeryDetails,
      otherProblemSurgeryYear: this.otherProblemSurgeryYear,
    };
  }
}
